<template>
    <div v-if="mounted">
        <transition-group name="fade">
            <div v-if="!editingAddress" key="recap">
                <div key="addNewAddress" class="mb-3">
                    <button class="btn btn-primary btn-sm" :disabled="!canAddNewAddress" @click="newAddress"><i
                            class="fa-solid fa-plus"></i>
                        {{ t.shipping_details.add_new_address }} ({{ t.global.max }} {{
                            config.addresses_management.max_addresses }})
                    </button>
                </div>
                <div v-if="addresses.length" class="row">
                    <div v-for="address in addresses" :key="address.id" class="d-flex mb-3 mb-md-0" :class="{ 'col-md-4': context == 'checkout', 'col-md-5': context != 'checkout' }">
                        <div class="border position-relative w-100"
                            :class="{ 'selected-address': selectedAddress && address.id == selectedAddress.id }">
                            <div class="p-3" :class="{ 'pb-5': context == 'checkout' }">
                                <div v-if="address.is_default" class="fw-bold text-muted mb-1">{{ t.global.default }}
                                </div>
                                <div class="fw-bold mb-1">{{ address.address_name }}</div>
                                <div>
                                    <div v-if="config.customer_name_fields == 2">{{ address.first_name }} {{ address.last_name }}</div>
                                    <div v-else>{{ address.name }}</div>
                                    {{ address.phone }}<br />
                                    {{ address.street }}<br />
                                    {{ address.city }}<br />
                                    {{ address.zip_code }}<br />
                                    <div v-if="!config.default_country">
                                        {{ country_list[address.country] }}
                                    </div>
                                    <div v-if="address.note" class="mt-3">
                                        <span class="fw-bold">{{ t.shipping_details.note }}:</span> <span class="fst-italic">"{{ address.note }}"</span>
                                    </div>
                                </div>
                                <div class="mt-2">
                                    <div class="btn btn-link btn-sm ps-0" @click="editAddress(address)">
                                        <i class="fa fa-pencil-alt"></i> {{ t.global.edit }}
                                    </div>
                                    <div class="btn btn-link btn-sm" @click="deleteAddress(address.id)">
                                        <i class="fa fa-times"></i> {{ t.global.delete }}
                                    </div>
                                    <div class="btn btn-link btn-sm" v-if="!address.is_default"
                                        @click="setAddressAsDefault(address.id)">
                                        <i class="fa fa-star"></i>
                                        {{ t.global.set_default }}
                                    </div>
                                </div>
                            </div>
                            <div v-if="context == 'checkout'" class="position-absolute bottom-0 w-100">
                                <div v-if="selectedAddress && address.id == selectedAddress.id" class="bg-secondary text-white text-center p-1">{{ t.global.selected }}</div>
                                <div v-else @click="selectAddress(address)"
                                    class="btn btn-light p-1 w-100">{{ t.global.select }}</div>
                            </div>
                        </div>
                    </div>
                </div>
                <div v-else>{{ t.shipping_details.no_address }}</div>
                <div v-if="context == 'checkout'" class="mt-3">
                    <button class="btn btn-secondary me-2" type="button" @click="$listeners.back()">{{ t.global.back }}</button>
                    <button class="btn btn-primary" v-if="selectedAddress" @click="$listeners.saveShippingDetails(selectedAddress)">{{ t.checkout.next_step }}</button>
                </div>
            </div>
            <form @submit.prevent="saveAddress" v-if="editingAddress"
                key="addressForm" :class="{'col-md-8': context != 'checkout'}" @keypress.enter="saveAddress">
                <div class="btn btn-link btn-sm ps-0 mb-3" @click="hideAddressForm"
                    v-if="config.modules.addresses_management && !isGuest"><i class="fa fa-chevron-left"></i>
                    {{ t.global.back }}</div>
                <div v-if="config.modules.addresses_management && !isGuest" class="mb-3">
                    <label for="address_name">{{ t.shipping_details.address_name }}<span
                            class="text-danger">*</span></label>
                    <input type="text" name="address_name" id="address_name" class="form-control"
                        v-model="formAddress.address_name" :disabled="formAddress.busy"/>
                    <div class="small text-danger" v-if="formAddress.errors.has('address_name')"
                        v-html="formAddress.errors.get('address_name')"></div>
                </div>
                <div v-if="config.customer_name_fields == 2">
                    <div class="mb-3" v-if="config.customer_name_fields == 2">
                        <label for="first_name">{{ t.personal_details.first_name }}<span class="text-danger">*</span></label>
                        <input type="text" name="first_name" id="first_name" class="form-control"
                            v-model="formAddress.first_name" :disabled="formAddress.busy" />
                        <div class="small text-danger" v-if="formAddress.errors.has('first_name')"
                            v-html="formAddress.errors.get('first_name')"></div>
                    </div>
                    <div class="mb-3">
                        <label for="last_name">{{ t.personal_details.last_name }}<span class="text-danger">*</span></label>
                        <input type="text" name="last_name" id="last_name" class="form-control"
                            v-model="formAddress.last_name" :disabled="formAddress.busy" />
                        <div class="small text-danger" v-if="formAddress.errors.has('last_name')"
                            v-html="formAddress.errors.get('last_name')"></div>
                    </div>
                </div>
                <div v-else>
                    <div class="mb-3">
                        <label for="name">{{ t.personal_details.name }}<span class="text-danger">*</span></label>
                        <input type="text" name="name" id="name" class="form-control"
                            v-model="formAddress.name" :disabled="formAddress.busy" />
                        <div class="small text-danger" v-if="formAddress.errors.has('name')"
                            v-html="formAddress.errors.get('name')"></div>
                    </div>
                </div>
                <div class="mb-3">
                    <label for="phone">{{ t.personal_details.phone }}<span class="text-danger">*</span></label>
                    <input type="text" name="phone" id="phone" class="form-control" v-model="formAddress.phone" :disabled="formAddress.busy" />
                    <div class="small text-danger" v-if="formAddress.errors.has('phone')"
                        v-html="formAddress.errors.get('phone')"></div>
                </div>
                <div class="mb-3">
                    <label for="street">{{ t.shipping_details.street }}<span class="text-danger">*</span></label>
                    <textarea name="street" id="street" rows="3" class="form-control"
                        v-model="formAddress.street" :disabled="formAddress.busy"></textarea>
                    <div class="small text-danger" v-if="formAddress.errors.has('street')"
                        v-html="formAddress.errors.get('street')"></div>
                </div>
                <div class="mb-3">
                    <label for="city">{{ t.shipping_details.city }}<span class="text-danger">*</span></label>
                    <input type="text" name="city" id="city" class="form-control" v-model="formAddress.city" :disabled="formAddress.busy" />
                    <div class="small text-danger" v-if="formAddress.errors.has('city')"
                        v-html="formAddress.errors.get('city')"></div>
                </div>
                <div class="mb-3">
                    <label for="zip_code">{{ t.shipping_details.zip_code }}<span
                            v-if="address_rules['zip_code'] == 'required'" class="text-danger">*</span></label>
                    <input type="text" name="zip_code" id="zip_code" class="form-control" v-model="formAddress.zip_code" :disabled="formAddress.busy" />
                    <div class="small text-danger" v-if="formAddress.errors.has('zip_code')"
                        v-html="formAddress.errors.get('zip_code')"></div>
                </div>
                <div class="mb-3" v-if="!config.default_country">
                    <label for="country">{{ t.shipping_details.country }}<span class="text-danger">*</span></label>
                    <select class="form-select" name="country" id="country" v-model="formAddress.country" :disabled="formAddress.busy">
                        <option value=""></option>
                        <option v-for="(countryName, countryCode) in country_list" :value="countryCode">{{ countryName }}
                        </option>
                    </select>
                    <div class="small text-danger" v-if="formAddress.errors.has('country')"
                        v-html="formAddress.errors.get('country')"></div>
                </div>
                <div class="mb-3">
                    <label for="note">{{ t.shipping_details.note }}</label>
                    <input type="text" name="note" id="note" class="form-control"
                        v-model="formAddress.note" :disabled="formAddress.busy" />
                    <div class="small text-danger" v-if="formAddress.errors.has('note')"
                        v-html="formAddress.errors.get('note')"></div>
                </div>
                <div class="text-muted mb-3" v-if="config.modules.gdpr">*{{ t.account.info_collect_personal_data }}</div>
                 <button v-if="context == 'checkout'" class="btn btn-secondary me-2" type="button" @click="$listeners.back()">{{ t.global.back }}</button>
                <div class="btn btn-primary" @click="saveAddress">{{ t.global.save }}</div>
            </form>
        </transition-group>
    </div>
</template>

<script>
import Form from 'vform'
export default {
    props: ['context', 'user'],
    data() {
        return {
            t: window.t,
            config: window.config,
            country_list: window.country_list,
            address_rules: window.address_rules,
            mounted: false,
            addresses: [],
            formAddress: null,
            selectedAddress: null,
            editingAddress: false,
        }
    },
    mounted() {
        this.getAddresses()
    },
    computed: {
        isGuest() {
            return this.context == 'checkout' && !(this.user && this.user.id)
        },
        canAddNewAddress() {
            return !this.isGuest
                && this.config.modules.addresses_management
                && this.addresses.length < this.config.addresses_management.max_addresses
        },
    },
    methods: {
        getAddresses() {
            axios.get('/ajax/addresses')
                .then(response => {
                    this.addresses = response.data
                    if (this.addresses.length) {
                        if (this.context == 'checkout' && this.addresses.length && config.modules.addresses_management && !this.isGuest) {
                            this.selectedAddress = this.addresses.find(address => address.is_default) ?? this.addresses[0]
                        }
                    }
                    if (!config.modules.addresses_management || this.isGuest || this.addresses.length == 0) {
                        this.addresses.push({
                            address_name: this.user ? this.user.customer.street : '',
                            first_name: this.user ? this.user.customer.first_name : '',
                            last_name: this.user ? this.user.customer.last_name : '',
                            name: this.user ? this.user.customer.name : '',
                            phone: this.user ? this.user.customer.phone : '',
                            street: this.user ? this.user.customer.street : '',
                            city: this.user ? this.user.customer.city : '',
                            zip_code: this.user ? this.user.customer.zip_code : '',
                            country: this.user ? this.user.customer.country : '',
                            phone: this.user ? this.user.customer.phone : '',
                        })
                        this.formAddress = new Form(this.addresses[0])
                        this.editingAddress = true
                        this.mounted = true
                    } else {
                        this.editingAddress = false
                        this.mounted = true
                    }
                    resolve();
                })
                .catch(error => {
                    console.log(error)
                })
        },
        hideAddressForm() {
            this.editingAddress = false
            this.formAddress = null
        },
        setAddressAsDefault(id) {
            axios.post('/ajax/set-address-as-default', { id: id })
                .then(response => {
                    if (response.data.success) {
                        this.addresses = response.data.addresses
                        this.notify({ text: response.data.message, type: 'success' })
                    } else {
                        this.notify({ text: response.data.message, type: 'error' })
                    }
                })
                .catch(error => {
                    console.log(error)
                })
        },
        deleteAddress(id) {
            if (!confirm(this.t.global.are_you_sure)) {
                return
            }
            axios.post('/ajax/delete-address', { id: id })
                .then(response => {
                    if (response.data.success) {
                        this.addresses = response.data.addresses
                        this.notify({ text: response.data.message, type: 'success' })
                        this.selectedAddress = null
                    } else {
                        this.notify({ text: response.data.message, type: 'error' })
                    }
                })
                .catch((error) => { Vue.notify({ text: error.message, type: 'error' }) })
        },
        newAddress() {
            if (this.canAddNewAddress) {
                this.formAddress = new Form({
                    id: null,
                    address_name: '',
                    first_name: '',
                    last_name: '',
                    name: '',
                    phone: '',
                    street: '',
                    city: '',
                    zip_code: '',
                    country: '',
                    phone: '',
                })
                this.editingAddress = true
            }
        },
        editAddress(address) {
            this.formAddress = new Form(address)
            this.editingAddress = true
        },
        selectAddress(address) {
            this.selectedAddress = address
        },
        submitSelectedAddress() {
            this.$listeners.saveShippingDetails(this.selectedAddress)
        },
        saveAddress() {
            this.formAddress.post('/ajax/save-address')
                .then(response => {
                    if (response.data.success) {
                        this.addresses = response.data.addresses
                        this.notify({ text: response.data.message, type: 'success' })
                        if (this.config.modules.addresses_management && !this.isGuest) {
                            this.hideAddressForm()
                        }
                        if ((!this.config.modules.addresses_management || this.isGuest)
                        && this.context == 'checkout') {
                            this.$listeners.saveShippingDetails()
                        }
                        // if only one address, select it
                        if (this.addresses.length == 1) {
                            this.selectedAddress = this.addresses[0]
                        }
                    } else {
                        this.notify({ text: response.data.message, type: 'error' })
                    }
                })
                // .catch((error) => { Vue.notify({ text: error.message, type: 'error' }) })
        }
    },
}
</script>
